/**
 * Types for the generic metric widget system.
 *
 * A widget's config fully describes what data to fetch and how to render it.
 * The GenericMetricWidget component interprets this config, calls the
 * performance API, and delegates to the appropriate chart renderer.
 */

/**
 * Chart types supported by the generic widget.
 *
 * Existing renderers: bar, line, heatmap, table, map.
 * Phase C additions: treemap (hierarchy rollup), bullet (goal tracking),
 * slope (FY-vs-FY delta), calendar (seasonality), boxplot (distribution),
 * sankey (two-level flow), scatter (outliers).
 */
export type ChartType =
  | 'bar'
  | 'line'
  | 'heatmap'
  | 'table'
  | 'map'
  | 'treemap'
  | 'bullet'
  | 'slope'
  | 'calendar'
  | 'boxplot'
  | 'sankey'
  | 'scatter'

/**
 * Dimensions that can be used as the X axis or grouping dimension.
 * 'hierarchy' dimensions (agency, MAJCOM, subcom1) trigger a JOIN with
 * dim_offices in the API; others group directly on fact-table columns.
 *
 * 'state' and 'congressional_district' route to the geo-performance endpoint
 * backed by geo_performance_fact (DIB surface).
 */
export type GroupByDimension =
  | 'fiscal_year'
  | 'fiscal_quarter'
  | 'agency'
  | 'sub_agency'
  | 'macom'
  | 'subcom1'
  | 'awarding_office_code'
  | 'psc_level1_code'
  | 'psc_level2_code'
  | 'color_of_money'
  | 'vehicle_category'
  | 'extent_competed'
  | 'is_ot'
  | 'state'
  | 'congressional_district'

/**
 * Metrics available from performance_fact and geo_performance_fact aggregations.
 *
 * Note: there is no `avg_days_to_award` here. performance_fact's FAR rows come
 * from USAspending, which carries only a solicitation *id*, not a date — so a
 * valid solicitation→award lead time can't be computed. Speed lives in the
 * dedicated speed_to_contract mart (p25/p50/p75/p90 solicitation→signing).
 */
export type MetricKey =
  | 'award_count'
  /** Distinct contract count (initials only, mods excluded). Useful for "how many distinct procurements?" vs award_count "how many actions?". */
  | 'unique_award_count'
  | 'total_obligated'
  | 'full_open_pct'
  | 'not_competed_pct'
  /** Follow-on share — share of awards that follow a prior contract (incumbent retention indicator). */
  | 'follow_on_pct'
  /** Share of awards using Other Transactions vs FAR. Backend M.M1 added this rate column. */
  | 'ot_award_pct'
  /** Phase M.M7 — gross positive obligations (excludes deobligations). */
  | 'gross_obligated'
  | 'avg_days_to_award'
  /** Phase M.M7 — absolute sum of negative-action dollars. */
  | 'deobligated_dollars'
  /** Phase M.M7 — share of dollars taken back via negative actions vs gross obligations. */
  | 'deobligation_pct'
  | 'avg_offers_received'
  | 'single_offer_pct'
  | 'sb_dollar_pct'
  | 'sb_award_pct'
  | 'set_aside_pct'
  | 'sb_awards'
  | 'sb_dollars'
  | 'count_8a'
  | 'count_hubzone'
  | 'count_sdvosb'
  | 'count_wosb'
  /** OT obligated-dollar share (parcat branch). */
  | 'ot_dollar_pct'
  /** Distinct recipient count — only available from geo_performance endpoint */
  | 'vendor_count'

/** Which vendor location to attribute spend to in geo views */
export type LocationKind = 'recipient' | 'pop'

/** The full widget configuration stored in DashboardWidget.config */
export interface GenericWidgetConfig {
  /** Primary grouping dimension (X axis / rows) */
  group_by: GroupByDimension
  /** Metric(s) to display (Y axis / cell values) */
  metrics: MetricKey[]
  /** Optional secondary dimension for series (e.g., group bars by FY) */
  series_by?: GroupByDimension
  /** Chart type */
  chart_type: ChartType
  /** Filters applied to the query — single year, array, or comma-separated string */
  fiscal_year?: number | number[] | string
  fiscal_quarter?: number
  sub_agency_code?: string
  office_code?: string
  psc_level1_code?: string
  psc_level2_code?: string
  color_of_money?: string
  vehicle_category?: string
  extent_competed?: string
  is_ot?: boolean
  /** Hierarchy scope filters */
  macom_code?: string
  agency_code?: string
  portfolio_group?: string
  /** Geo filters (geo_performance_fact) — state_code accepts a single code or an array (serialized comma-joined). */
  state_code?: string | string[]
  congressional_district?: string
  /** Recipient HQ vs Place of Performance (defaults to 'recipient' on geo endpoint) */
  location_kind?: LocationKind
  /** Sort and limit */
  sort_by?: string
  limit?: number
  /** Optional third metric mapped to symbol size (scatter renderer only). */
  size_metric?: MetricKey
  /** When true, slope-chart y-axis becomes rank (1 = best) instead of value. */
  as_rank?: boolean
}

/** A single row returned from the performance API after aggregation */
export interface PerfRow {
  [key: string]: string | number | boolean | null
}

/** Drill-down breadcrumb entry */
export interface DrillLevel {
  dimension: GroupByDimension
  code?: string
  label: string
}

/** Hierarchy drill-down mapping: which dimension comes next when you click */
export const DRILL_HIERARCHY: Partial<Record<GroupByDimension, GroupByDimension>> = {
  agency: 'sub_agency',
  sub_agency: 'macom',
  macom: 'subcom1',
  subcom1: 'awarding_office_code',
  fiscal_year: 'fiscal_quarter',
  state: 'congressional_district',
  congressional_district: 'fiscal_year',
  psc_level1_code: 'psc_level2_code',
}

/** Dimensions that need the hierarchy_level API param (JOIN with dim_offices) */
export const HIERARCHY_DIMENSIONS = new Set<GroupByDimension>([
  'agency', 'sub_agency', 'macom', 'subcom1',
])

/** Dimensions that route to the geo_performance endpoint */
export const GEO_DIMENSIONS = new Set<GroupByDimension>([
  'state', 'congressional_district', 'psc_level2_code',
])

/** Human-readable labels for dimension values */
export const DIMENSION_LABELS: Record<GroupByDimension, string> = {
  fiscal_year: 'Fiscal Year',
  fiscal_quarter: 'Quarter',
  agency: 'Agency',
  sub_agency: 'Branch / Sub-Agency',
  macom: 'Major Command (MAJCOM)',
  subcom1: 'Sub-Command',
  awarding_office_code: 'Office',
  psc_level1_code: 'PSC Category',
  psc_level2_code: 'PSC Category (L2)',
  color_of_money: 'Color of Money',
  vehicle_category: 'Vehicle Category',
  extent_competed: 'Competition Status',
  is_ot: 'FAR/OT',
  state: 'State',
  congressional_district: 'Congressional District',
}

/** PSC Level 1 code → display name. Seeded from Velocity-Defense/data:govdata/dbt/seeds/psc_hierarchy.csv. */
export const PSC_L1_NAMES: Record<string, string> = {
  '1': 'IT',
  '2': 'Professional Services',
  '3': 'Security and Protection',
  '4': 'Facilities & Construction',
  '5': 'Industrial Products & Services',
  '6': 'Office Management',
  '7': 'Transportation and Logistics Services',
  '8': 'Travel & Lodging',
  '9': 'Human Capital',
  '10': 'Medical',
  '11': 'Aircraft, Ships/Submarines & Land Vehicles',
  '12': 'Weapons & Ammunition',
  '13': 'Electronic & Communication Equipment',
  '14': 'Sustainment S&E',
  '15': 'Clothing, Textiles & Subsistence S&E',
  '16': 'Miscellaneous S&E',
  '17': 'Research and Development',
  '18': 'Equipment Related Services',
  '19': 'Electronic & Communication Services',
}

/** PSC Level 2 code → display name. Seeded from Velocity-Defense/data:govdata/dbt/seeds/psc_hierarchy.csv. */
export const PSC_L2_NAMES: Record<string, string> = {
  '1.1': 'Product',
  '1.2': 'Capability As a Service',
  '1.3': 'IT Professional Service (Labor)',
  '2.0': 'Other Professional Services',
  '2.1': 'Social Services',
  '2.2': 'Legal Services',
  '2.3': 'Management Advisory Services',
  '2.4': 'Marketing and Distribution',
  '2.5': 'Public Relations and Professional Communications Services',
  '2.6': 'Real Estate Services',
  '2.7': 'Trade Policy and Services',
  '2.8': 'Technical and Engineering Services (non-IT)',
  '2.9': 'Financial Services',
  '3.0': 'Other Security',
  '3.1': 'Security Animals & Related Svcs',
  '3.2': 'Security Systems',
  '3.3': 'Security Services',
  '4.1': 'Construction Related Materials',
  '4.2': 'Construction Related Services',
  '4.3': 'Facility Related Materials',
  '4.4': 'Facility Related Services',
  '4.5': 'Facilities Purchase & Lease',
  '5.1': 'Machinery & Components',
  '5.2': 'Fire/Rescue/Safety/Environmental Protection Equipment',
  '5.3': 'Hardware & Tools',
  '5.4': 'Test & Measurement Supplies',
  '5.5': 'Industrial Products Install / Maintenance / Repair',
  '5.6': 'Basic Materials',
  '5.7': 'Oils, Lubricants, and Waxes',
  '6.1': 'Office Management Products',
  '6.2': 'Office Management Services',
  '6.3': 'Furniture',
  '7.1': 'Package Delivery & Packaging',
  '7.2': 'Logistics Support Services',
  '7.4': 'Transportation of Things',
  '7.5': 'Motor Vehicles',
  '7.6': 'Transportation Equipment',
  '7.7': 'Fuels',
  '8.1': 'Passenger Travel',
  '8.2': 'Lodging',
  '8.3': 'Travel Agent & Misc Svcs',
  '9.4': 'Specialized Educational Services',
  '9.5': 'Vocational Training',
  '9.6': 'Human Resources Services',
  '10.1': 'Drugs and Pharmaceutical Products',
  '10.2': 'Medical Equipment and Accessories and Supplies',
  '10.3': 'Healthcare Services',
  '11.1': 'Aircraft',
  '11.2': 'Land Combat Vehicles',
  '11.3': 'Ships & Submarines',
  '12.1': 'Ammunition & Explosives',
  '12.2': 'Fire Control',
  '12.3': 'Guided Missiles',
  '12.4': 'Guns',
  '12.5': 'Nuclear Ordnance',
  '12.6': 'Weapons',
  '13.1': 'Communication Equipment',
  '13.2': 'Detection & Coherent Radiation Eq',
  '13.3': 'Electrical & Electronics Equipment',
  '13.4': 'Night Vision Eq',
  '14.0': 'Other Sustainment',
  '14.1': 'Drones',
  '14.2': 'Engines, Components & Spt Eq',
  '14.3': 'Materials',
  '14.4': 'Supply Parts',
  '14.5': 'Support Ships & Small Craft',
  '14.6': 'Training Aids and Devices',
  '15.1': 'Subsistence',
  '15.2': 'Textiles, Clothing & Equipage',
  '16.2': 'S&E Not Classified Elsewhere',
  '17.1': 'Agriculture R&D',
  '17.2': 'Community Service R&D',
  '17.3': 'Defense System R&D',
  '17.6': 'Education R&D',
  '17.7': 'Energy R&D',
  '17.8': 'Environmental Protection R&D',
  '17.9': 'General Science & Technology R&D',
  '17.10': 'Housing R&D',
  '17.11': 'Income Security R&D',
  '17.12': 'International Affairs R&D',
  '17.13': 'Medical R&D',
  '17.16': 'Space R&D',
  '17.17': 'Transportation R&D',
  '18.1': 'Maintenance, Repair and Overhaul',
  '18.2': 'Equipment Modification',
  '18.3': 'Installation of Equipment',
  '18.4': 'Quality Control',
  '18.5': 'Technical Representative Services',
  '18.6': 'Purchases & Leases',
  '18.7': 'Salvage Services',
  '19.1': 'Equipment Maintenance',
  '19.2': 'Equipment Leases',
}

/**
 * Resolve a row's value for a dimension to a human display label.
 * Centralises the logic that each chart renderer used to duplicate.
 */
/**
 * Resolve a row's stable key for a dimension (for grouping / map lookups).
 * For hierarchy dims, the API JOINs dim_offices and returns `entity_code`
 * rather than the requested column, so naive `row[dim]` is empty.
 */
export function getDimensionKey(row: PerfRow, dim: GroupByDimension): string {
  if (HIERARCHY_DIMENSIONS.has(dim) && row.entity_code != null) {
    return String(row.entity_code)
  }
  return String(row[dim] ?? '')
}

export function getDimensionLabel(row: PerfRow, dim: GroupByDimension): string {
  if (HIERARCHY_DIMENSIONS.has(dim) && row.entity_name) return row.entity_name as string
  if (dim === 'fiscal_year') return `FY${row.fiscal_year}`
  if (dim === 'fiscal_quarter') return `Q${row.fiscal_quarter}`
  if (dim === 'is_ot') return row.is_ot ? 'Other Transaction' : 'FAR'
  if (dim === 'psc_level1_code') {
    const code = String(row.psc_level1_code ?? '')
    const name = PSC_L1_NAMES[code]
    return name ? `${code} — ${name}` : code || 'Unknown'
  }
  if (dim === 'psc_level2_code') {
    const code = String(row.psc_level2_code ?? '')
    const name = PSC_L2_NAMES[code]
    return name ? `${code} — ${name}` : code || 'Unknown'
  }
  return String(row[dim] ?? 'Unknown')
}

/** Human-readable labels for metrics */
export const METRIC_LABELS: Record<MetricKey, string> = {
  award_count: 'Awards (actions)',
  unique_award_count: 'Distinct Contracts',
  total_obligated: 'Obligated ($)',
  full_open_pct: 'Full & Open %',
  not_competed_pct: 'Not Competed %',
  follow_on_pct: 'Follow-On %',
  ot_award_pct: 'Other Transaction %',
  gross_obligated: 'Gross Obligated ($)',
  avg_days_to_award: 'Time to Contract (median days)',
  deobligated_dollars: 'Deobligated ($)',
  deobligation_pct: 'Deobligation Rate',
  avg_offers_received: 'Avg Offers Received',
  single_offer_pct: 'Single Offer %',
  sb_dollar_pct: 'Small Business $ %',
  sb_award_pct: 'Small Business Award %',
  set_aside_pct: 'Set-Aside %',
  sb_awards: 'SB Awards',
  sb_dollars: 'SB Dollars',
  count_8a: '8(a) Awards',
  count_hubzone: 'HUBZone Awards',
  count_sdvosb: 'SDVOSB Awards',
  count_wosb: 'WOSB Awards',
  ot_dollar_pct: 'OT Dollar %',
  vendor_count: 'Unique Vendors',
}

/** Format a metric value for display */
export function formatMetric(key: MetricKey, value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (key.endsWith('_pct')) return `${(value * 100).toFixed(1)}%`
  if (key === 'avg_days_to_award') return `${Math.round(value)}d`
  if (key === 'total_obligated' || key === 'sb_dollars') {
    const abs = Math.abs(value)
    if (abs >= 1e9) return `$${(value / 1e9).toFixed(1)}B`
    if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
    if (abs >= 1e3) return `$${(value / 1e3).toFixed(0)}K`
    return `$${value.toFixed(0)}`
  }
  if (key === 'avg_offers_received') return value.toFixed(1)
  return value.toLocaleString()
}
